import { InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'

import { toast } from '@/hooks/use-toast'

import MemePostEditorContainer from '@/components/meme/meme-post-editor-container'
import PinnedPostsSection from '@/components/meme/pinned-posts-section'
import PostMemeCard from '@/components/meme/post-meme-card'
import { PostMemeCardSkeletonList } from '@/components/meme/post-meme-card-skeleton'

import { useAuth } from '@/contexts/auth-context'
import { useMemePostModal } from '@/contexts/meme-post-modal-context'
import memeService from '@/services/meme.service'
import { isAdmin, isInstructor } from '@/utils/admin-utils'

import { KeysetListResponse } from '@/types/list.type'
import { EmojiData, MemePost, PinnedPost } from '@/types/meme.interface'

type ListMemeContainerProps = {
  channelId?: string
  showPinnedPosts?: boolean
  showPostEditor?: boolean
  postsPerPage?: number
  layout?: 'list' | 'grid'
}

function ListMemeContainer({
  channelId,
  showPinnedPosts = true,
  showPostEditor = true,
  postsPerPage = 10,
  layout = 'list',
}: ListMemeContainerProps) {
  const queryClient = useQueryClient()
  const { isAuthenticated, user } = useAuth()
  const [editingPost, setEditingPost] = React.useState<MemePost | null>(null)

  const canPin = isAdmin(user) || isInstructor(user)

  const searchParams = useSearchParams()
  const tags = searchParams.get('tags') || undefined

  const {
    data: postPages,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['meme-post-list', channelId ?? 'all', tags, postsPerPage],
    queryFn: ({ pageParam }) => {
      const query = {
        pageSize: postsPerPage,
        cursor: pageParam,
        filters: tags ? JSON.stringify({ tags }) : undefined,
      }

      if (channelId) {
        return memeService.getChannelMemes(channelId, query)
      }
      return memeService.getMemes(query)
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => (lastPage.meta.hasNextPage ? lastPage.meta.nextCursor ?? undefined : undefined),
    staleTime: 60_000,
  })

  const posts = React.useMemo(
    () => postPages?.pages.flatMap(page => page.data) ?? [],
    [postPages]
  )

  // Fetch pinned post IDs for this channel so we can mark them in the regular list
  const { data: pinnedPosts } = useQuery({
    queryKey: ['pinned-posts', channelId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => memeService.getPinnedPosts(channelId!),
    enabled: !!channelId && showPinnedPosts,
  })

  const pinnedPostIds = React.useMemo(
    () => new Set((pinnedPosts ?? []).map((p: PinnedPost) => p.post.id)),
    [pinnedPosts]
  )

  const handleToggleReaction = async (postId: string, reactionType = 'like', emojiData?: EmojiData) => {
    if (!isAuthenticated) {
      // You could show a login modal or toast here
      // eslint-disable-next-line no-console
      console.warn('User must be authenticated to react')
      return
    }

    try {
      queryClient.setQueriesData(
        { queryKey: ['meme-post-list', channelId ?? 'all'] },
        (oldData: InfiniteData<KeysetListResponse<MemePost>> | undefined) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map(page => ({
              ...page,
              data: page.data.map((post: MemePost) => {
                if (post.id === postId) {
                  const wasUserReacted = post.userReacted
                  return {
                    ...post,
                    userReacted: !wasUserReacted,
                    reactionCount: wasUserReacted ? post.reactionCount - 1 : post.reactionCount + 1,
                  }
                }
                return post
              }),
            })),
          }
        }
      )

      await memeService.toggleReaction({ postId, reactionType, emojiData })

      queryClient.invalidateQueries({ queryKey: ['meme-post-list', channelId ?? 'all'] })
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: ['pinned-posts', channelId] })
      }
    } catch (error) {
      queryClient.invalidateQueries({ queryKey: ['meme-post-list', channelId ?? 'all'] })
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: ['pinned-posts', channelId] })
      }
      // eslint-disable-next-line no-console
      console.error('Failed to toggle reaction:', error)
    }
  }

  const handlePinPost = React.useCallback(
    async (postId: string) => {
      if (!channelId) return
      try {
        await memeService.pinPost(channelId, postId)
        queryClient.invalidateQueries({ queryKey: ['pinned-posts', channelId] })
        toast({ title: 'Thành công', description: 'Đã ghim bài viết' })
      } catch {
        toast({ title: 'Lỗi', description: 'Không thể ghim bài viết', variant: 'destructive' })
      }
    },
    [channelId, queryClient]
  )

  const handleUnpinPost = React.useCallback(
    async (postId: string) => {
      if (!channelId) return
      try {
        await memeService.unpinPost(channelId, postId)
        queryClient.invalidateQueries({ queryKey: ['pinned-posts', channelId] })
        toast({ title: 'Thành công', description: 'Đã bỏ ghim bài viết' })
      } catch {
        toast({ title: 'Lỗi', description: 'Không thể bỏ ghim bài viết', variant: 'destructive' })
      }
    },
    [channelId, queryClient]
  )

  const handleDelete = async (postId: string) => {
    await memeService.deleteMeme(postId)
    queryClient.invalidateQueries({ queryKey: ['meme-post-list'] })
  }

  const handleEdit = (post: MemePost) => {
    setEditingPost(post)
  }

  const handleCloseEdit = () => {
    setEditingPost(null)
  }

  const hasNextPageRef = React.useRef(hasNextPage)
  const isFetchingNextPageRef = React.useRef(isFetchingNextPage)

  React.useEffect(() => {
    hasNextPageRef.current = hasNextPage
  }, [hasNextPage])

  React.useEffect(() => {
    isFetchingNextPageRef.current = isFetchingNextPage
  }, [isFetchingNextPage])

  React.useEffect(() => {
    if (!hasNextPage) return

    const hasUserScrolledRef = { current: false }
    const LOAD_THRESHOLD_PX = 320
    let ticking = false

    const getDistanceFromBottom = () => {
      const scrollRoot = document.querySelector<HTMLElement>('[data-meme-feed-scroll]')
      if (scrollRoot && scrollRoot.scrollHeight > scrollRoot.clientHeight + 1) {
        return scrollRoot.scrollHeight - scrollRoot.scrollTop - scrollRoot.clientHeight
      }

      return document.documentElement.scrollHeight - window.scrollY - window.innerHeight
    }

    const tryLoadMore = () => {
      if (!hasUserScrolledRef.current) return
      if (!hasNextPageRef.current || isFetchingNextPageRef.current) return
      if (getDistanceFromBottom() > LOAD_THRESHOLD_PX) return

      void fetchNextPage()
    }

    const handleScroll = () => {
      hasUserScrolledRef.current = true
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        tryLoadMore()
      })
    }

    const scrollRoot = document.querySelector<HTMLElement>('[data-meme-feed-scroll]')
    window.addEventListener('scroll', handleScroll, { passive: true })
    scrollRoot?.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      scrollRoot?.removeEventListener('scroll', handleScroll)
    }
  }, [channelId, fetchNextPage, hasNextPage, posts.length, tags])

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8">
      <>
        {showPostEditor && <MemePostEditorContainer editPost={editingPost} onCloseEdit={handleCloseEdit} />}

        {channelId && showPinnedPosts && (
          <PinnedPostsSection channelId={channelId} onToggleReaction={handleToggleReaction} />
        )}

        {isLoading && <PostMemeCardSkeletonList count={5} />}

        {!isLoading && posts.length ? (
          <>
            <div className={layout === 'grid' ? 'grid grid-cols-1 xl:grid-cols-2 gap-4' : 'space-y-4'}>
              {posts.map(post => (
                <PostMemeCard
                  post={post}
                  key={post.id}
                  onToggleReaction={handleToggleReaction}
                  isAuthenticated={isAuthenticated}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  isPinned={pinnedPostIds.has(post.id)}
                  onPin={canPin && channelId ? handlePinPost : undefined}
                  onUnpin={canPin && channelId ? handleUnpinPost : undefined}
                />
              ))}
            </div>
            {isFetching && !isLoading && (
              <div className="flex justify-center py-4">
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                  <span>Đang cập nhật...</span>
                </div>
              </div>
            )}
          </>
        ) : null}

        {!isLoading && posts.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">📝</div>
            <div className="text-gray-500 font-medium mb-1">Chưa có bài viết nào</div>
            <div className="text-gray-400 text-sm">Hãy là người đầu tiên chia sẻ nội dung thú vị!</div>
          </div>
        )}
      </>

      {hasNextPage ? (
        <div className="mt-10 flex min-h-8 justify-center py-6 md:py-12">
          {isFetchingNextPage ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
              <span>Đang tải...</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function SharedPostHandler() {
  const { openModalWithId } = useMemePostModal('MemePostModal')

  const postId = useSearchParams().get('postId') ?? null
  const handledIdRef = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!postId) {
      handledIdRef.current = null
      return
    }

    if (handledIdRef.current === postId) {
      return
    }

    openModalWithId(postId)
      .then(() => {
        handledIdRef.current = postId
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error opening shared post modal:', error)
        handledIdRef.current = null
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId])

  return null
}

export default ListMemeContainer
